import { useState } from 'react';
import Icon from './Icon';
import Button from './ui/Button';
import { Select, Textarea } from './ui/Input';
import { listMediaAssets, uploadMediaAsset, type MediaAsset } from '../lib/sections-api';
import { useAccounts } from '../contexts/AccountContext';

export interface SequenceItem {
  kind: 'text' | 'image' | 'video' | 'file';
  text: string | null;
  media_asset_id: string | null;
}

interface SequenceBuilderProps {
  items: SequenceItem[];
  onChange: (items: SequenceItem[]) => void;
}

export default function SequenceBuilder({ items, onChange }: SequenceBuilderProps) {
  const { selectedAccountId } = useAccounts();
  const [newKind, setNewKind] = useState<'text' | 'image' | 'video' | 'file'>('text');
  const [newText, setNewText] = useState('');
  const [uploading, setUploading] = useState(false);
  const [assets, setAssets] = useState<MediaAsset[]>([]);

  const loadAssets = async (): Promise<void> => {
    try {
      const list = await listMediaAssets();
      setAssets(list);
    } catch {
      setAssets([]);
    }
  };

  const addItem = (): void => {
    if (newKind === 'text') {
      if (newText.trim() === '') return;
      onChange([...items, { kind: 'text', text: newText.trim(), media_asset_id: null }]);
      setNewText('');
    } else {
      // media item: pick from uploaded assets or upload
      const asset = assets.find((a) => a.kind === newKind);
      if (asset !== undefined) {
        onChange([...items, { kind: newKind, text: null, media_asset_id: asset.id }]);
      }
    }
  };

  const handleUpload = async (file: File): Promise<void> => {
    if (selectedAccountId === null) return;
    setUploading(true);
    try {
      const asset = await uploadMediaAsset(selectedAccountId, file);
      setAssets((prev) => [asset, ...prev]);
      onChange([...items, { kind: asset.kind, text: null, media_asset_id: asset.id }]);
    } catch {
      // ignore
    } finally {
      setUploading(false);
    }
  };

  const removeItem = (index: number): void => {
    onChange(items.filter((_, i) => i !== index));
  };

  const moveItem = (index: number, dir: -1 | 1): void => {
    const target = index + dir;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    const tmp = next[index];
    next[index] = next[target];
    next[target] = tmp;
    onChange(next);
  };

  return (
    <div className="space-y-3">
      <p className="text-xs font-medium text-slate-700">ترتیب محتوا (sequence)</p>

      {items.length > 0 && (
        <ol className="space-y-2">
          {items.map((item, index) => (
            <li
              key={index}
              className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"
            >
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white text-[10px] font-bold text-slate-500">
                {index + 1}
              </span>
              <Icon
                name={
                  item.kind === 'text'
                    ? 'dm'
                    : item.kind === 'image'
                      ? 'image'
                      : item.kind === 'video'
                        ? 'video'
                        : 'file'
                }
                className="h-4 w-4 shrink-0 text-slate-400"
              />
              <span className="min-w-0 flex-1 truncate text-xs text-slate-700">
                {item.kind === 'text' ? item.text : `${item.kind} — ${item.media_asset_id ?? ''}`}
              </span>
              <div className="flex shrink-0 gap-1">
                <button
                  type="button"
                  onClick={() => moveItem(index, -1)}
                  className="rounded p-1 text-slate-400 hover:bg-white hover:text-slate-600"
                  aria-label="بالا"
                >
                  <Icon name="chevron-down" className="h-3 w-3 rotate-180" />
                </button>
                <button
                  type="button"
                  onClick={() => moveItem(index, 1)}
                  className="rounded p-1 text-slate-400 hover:bg-white hover:text-slate-600"
                  aria-label="پایین"
                >
                  <Icon name="chevron-down" className="h-3 w-3" />
                </button>
                <button
                  type="button"
                  onClick={() => removeItem(index)}
                  className="rounded p-1 text-rose-400 hover:bg-white hover:text-rose-600"
                  aria-label="حذف"
                >
                  <Icon name="trash" className="h-3 w-3" />
                </button>
              </div>
            </li>
          ))}
        </ol>
      )}

      <div className="flex flex-wrap items-end gap-2 rounded-lg border border-dashed border-slate-300 p-3">
        <Select
          label="نوع"
          value={newKind}
          onChange={(e) => setNewKind(e.target.value as 'text' | 'image' | 'video' | 'file')}
          className="w-32"
        >
          <option value="text">متن</option>
          <option value="image">تصویر</option>
          <option value="video">ویدیو</option>
          <option value="file">فایل</option>
        </Select>
        {newKind === 'text' ? (
          <div className="min-w-0 flex-1">
            <Textarea
              label="متن"
              value={newText}
              onChange={(e) => setNewText(e.target.value)}
              placeholder="متن پیام..."
              rows={2}
            />
          </div>
        ) : (
          <div className="min-w-0 flex-1 space-y-1">
            <label className="block text-xs font-medium text-slate-700">فایل</label>
            <input
              type="file"
              accept={
                newKind === 'image'
                  ? 'image/png,image/jpeg'
                  : newKind === 'video'
                    ? 'video/mp4'
                    : 'application/pdf'
              }
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file !== undefined) void handleUpload(file);
              }}
              className="block w-full text-xs text-slate-500 file:mr-3 file:rounded-lg file:border-0 file:bg-brand-50 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-brand-700 hover:file:bg-brand-100"
              disabled={uploading}
            />
          </div>
        )}
        <Button size="sm" variant="secondary" onClick={addItem} disabled={uploading}>
          افزودن
        </Button>
      </div>

      {assets.length > 0 && (
        <button
          type="button"
          onClick={() => void loadAssets()}
          className="text-[11px] text-brand-600 hover:underline"
        >
          بارگذاری مجدد فایل‌ها
        </button>
      )}
    </div>
  );
}
